import { useCallback, useEffect, useRef, useState, type ChangeEvent, type FormEvent } from 'react'
import {
  Check, FileText, Hotel, KeyRound, Save, Search, TriangleAlert, UserRound, X,
} from 'lucide-react'
import { api, ApiError } from '../lib/api'
import { parseAccommodationCsv } from '../lib/accommodation-csv'
import type {
  AccommodationAssignment, AccommodationCounts, AccommodationFilter,
  AccommodationImportIssue, AccommodationImportResponse, AccommodationStatus,
} from '../lib/types'
import { useAuthed } from '../lib/session'
import { KEY_COLLECTION } from '../config/event'
import { Button } from '../ui/Button'
import { Masthead } from '../ui/Chrome'
import { Notice, Skeleton, useToast } from '../ui/Feedback'

/** Long enough that typing a name does not fire a request per keystroke, short enough to feel live. */
const DEBOUNCE_MS = 300
const PAGE_SIZE = 50
const SAVED_TICK_MS = 2400

const FILTERS: Array<{ value: AccommodationFilter; label: string }> = [
  { value: 'all', label: 'Everyone' },
  { value: 'unassigned', label: 'No room yet' },
  { value: 'assigned', label: 'Has a room' },
  { value: 'own', label: 'Own arrangement' },
  ...(KEY_COLLECTION ? [{ value: 'nokey' as const, label: 'Key not collected' }] : []),
]

/* "09:14" for the tick's own day, "Tue 09:14" otherwise: the desk works one day at a time. */
function keyTime(iso: string): string {
  const d = new Date(iso)
  const sameDay = d.toDateString() === new Date().toDateString()
  return new Intl.DateTimeFormat('en-MY', {
    timeZone: 'Asia/Kuala_Lumpur', hour: '2-digit', minute: '2-digit', hour12: false,
    ...(sameDay ? {} : { weekday: 'short' }),
  }).format(d)
}

const EMPTY_COUNTS: AccommodationCounts = {
  total: 0, assigned: 0, own_arrangement: 0, unassigned: 0, keys_collected: 0,
}

/**
 * An edit the organiser has made but not yet saved — a room typed, a room cleared, or
 * the own-arrangement toggle flipped. All three are equally a pending change.
 *
 * `stored` is the server's value at the moment they first touched the field, so a row
 * stays knowably dirty even after it scrolls out of the current result set. Drafts are
 * never seeded from a response — an earlier version overwrote every draft on each
 * search, silently destroying whatever had been typed but not saved.
 */
interface Draft {
  value: string
  stored: string
  status: AccommodationStatus
  storedStatus: AccommodationStatus
}

function isDirty(draft: Draft): boolean {
  return draft.value.trim() !== draft.stored || draft.status !== draft.storedStatus
}

/**
 * Which of the three header figures a row belongs to. Saving moves a row from one
 * bucket to another, which is cheaper than re-counting the whole event over the wire.
 */
type Bucket = 'assigned' | 'own_arrangement' | 'unassigned'

function bucketOf(row: { room_number: string | null; status: AccommodationStatus }): Bucket {
  if (row.status === 'own_arrangement') return 'own_arrangement'
  return row.room_number ? 'assigned' : 'unassigned'
}

export function AccommodationAdminRoute() {
  const session = useAuthed()
  const toast = useToast()

  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<AccommodationFilter>('all')
  const [participants, setParticipants] = useState<AccommodationAssignment[]>([])
  const [total, setTotal] = useState(0)
  const [counts, setCounts] = useState<AccommodationCounts>(EMPTY_COUNTS)
  const [drafts, setDrafts] = useState<Record<string, Draft>>({})
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [savingEmail, setSavingEmail] = useState<string | null>(null)
  const [savedEmail, setSavedEmail] = useState<string | null>(null)
  /** Page-level slot: search and permission failures only. Save failures belong to their card. */
  const [error, setError] = useState<string | null>(null)
  const [cardErrors, setCardErrors] = useState<Record<string, string>>({})

  const [fileName, setFileName] = useState<string | null>(null)
  const [importRows, setImportRows] = useState<Array<{
    line: number
    participant_email: string
    room_number: string
  }>>([])
  /*
   * Rejected rows come from two places and both have to stay on screen: rows this
   * client could not parse are never sent, so the server never mentions them, and
   * overwriting one list with the other hid half the work the organiser has to redo.
   */
  const [parseIssues, setParseIssues] = useState<AccommodationImportIssue[]>([])
  const [serverIssues, setServerIssues] = useState<AccommodationImportIssue[]>([])
  const [importing, setImporting] = useState(false)
  const [importResult, setImportResult] = useState<AccommodationImportResponse | null>(null)

  /** Guards against a slow early response landing on top of a later, narrower one. */
  const requestId = useRef(0)
  const firstLoad = useRef(true)

  const dirtyCount = Object.values(drafts).filter(isDirty).length

  /**
   * `offset > 0` appends rather than replaces — that is the Load more path. Without it
   * a 200-person event is a screen the organiser cannot reach the bottom of: "narrow
   * the search" is no help when the filter is *unassigned* and the whole point is that
   * they cannot name the people they are looking for.
   */
  const load = useCallback(async (
    value: string, chosen: AccommodationFilter, offset = 0,
  ) => {
    const id = requestId.current + 1
    requestId.current = id
    if (offset > 0) setLoadingMore(true)
    else setLoading(true)
    try {
      const result = await api.listAccommodationParticipants(
        session.at, value.trim(), chosen, PAGE_SIZE, offset,
      )
      if (requestId.current !== id) return
      setParticipants((current) => (
        offset > 0 ? [...current, ...result.participants] : result.participants
      ))
      setTotal(result.total)
      setCounts(result.counts)
      setError(null)
      // Keep only drafts still holding unsaved edits; a clean draft would just be a
      // stale copy of a value the server may since have changed.
      setDrafts((current) => {
        const next: Record<string, Draft> = {}
        Object.entries(current).forEach(([email, draft]) => {
          if (isDirty(draft)) next[email] = draft
        })
        return next
      })
    } catch (err) {
      if (requestId.current !== id) return
      // A failed Load more keeps the rows already on screen; only a fresh query clears them.
      if (offset === 0) {
        setParticipants([])
        setTotal(0)
      }
      setError(
        err instanceof ApiError && err.status === 403
          ? 'This account is not allowed to manage accommodation.'
          : 'Could not load participants. Check the connection and try again.',
      )
    } finally {
      if (requestId.current === id) {
        setLoading(false)
        setLoadingMore(false)
      }
    }
  }, [session.at])

  // The roster loads with no query at all, so the screen opens on the answer to the
  // question organisers actually have: who still has no room.
  useEffect(() => {
    if (firstLoad.current) {
      firstLoad.current = false
      void load(query, filter)
      return
    }
    const handle = window.setTimeout(() => void load(query, filter), DEBOUNCE_MS)
    return () => window.clearTimeout(handle)
  }, [query, filter, load])

  // Unsaved room numbers live only in this component; a reload would lose them silently.
  useEffect(() => {
    if (dirtyCount === 0) return
    const warn = (event: BeforeUnloadEvent) => event.preventDefault()
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [dirtyCount])

  useEffect(() => {
    if (!savedEmail) return
    const handle = window.setTimeout(() => setSavedEmail(null), SAVED_TICK_MS)
    return () => window.clearTimeout(handle)
  }, [savedEmail])

  function updateDraft(participant: AccommodationAssignment, patch: Partial<Draft>) {
    setDrafts((current) => {
      const existing = current[participant.email]
      const base: Draft = existing ?? {
        value: participant.room_number ?? '',
        stored: participant.room_number ?? '',
        status: participant.status,
        storedStatus: participant.status,
      }
      return { ...current, [participant.email]: { ...base, ...patch } }
    })
    setCardErrors((current) => {
      if (!(participant.email in current)) return current
      const next = { ...current }
      delete next[participant.email]
      return next
    })
  }

  function editRoom(participant: AccommodationAssignment, value: string) {
    updateDraft(participant, { value })
  }

  /*
   * Toggling marks the card dirty rather than saving on the spot, so it behaves like
   * the room field beside it and a mis-tap costs nothing until Save is pressed.
   * Switching on clears the room — the database refuses a row that is own_arrangement
   * and holds one — and switching back restores whatever was stored.
   */
  function toggleOwnArrangement(participant: AccommodationAssignment, own: boolean) {
    const stored = drafts[participant.email]?.stored ?? participant.room_number ?? ''
    updateDraft(participant, {
      status: own ? 'own_arrangement' : 'assigned',
      value: own ? '' : stored,
    })
  }

  function discardDrafts() {
    setDrafts({})
    setCardErrors({})
  }

  async function handleImportFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return

    setFileName(file.name)
    setImportResult(null)
    setServerIssues([])
    try {
      const parsed = parseAccommodationCsv(await file.text())
      setImportRows(parsed.rows)
      setParseIssues(parsed.issues)
    } catch {
      setImportRows([])
      setParseIssues([{ line: 1, reason: 'Could not read this file as text.' }])
    }
  }

  function clearImport() {
    setFileName(null)
    setImportRows([])
    setParseIssues([])
    setServerIssues([])
    setImportResult(null)
  }

  async function importRooms() {
    if (importRows.length === 0) return
    setImporting(true)
    setImportResult(null)
    try {
      const result = await api.importAccommodationRooms(
        session.at,
        importRows.map(({ participant_email, room_number }) => ({ participant_email, room_number })),
      )
      setImportResult(result)
      setServerIssues(result.issues)
      const written = result.imported + result.updated
      if (written > 0) {
        toast.show(`${written} room assignment${written === 1 ? '' : 's'} imported`, 'ok')
        // Counts and rows both move; refetch rather than guess which rows changed.
        await load(query, filter)
      } else {
        toast.show('Nothing was imported - every row was rejected', 'bad')
      }
    } catch {
      setImportResult(null)
      toast.show('Could not import the room assignments. Try again.', 'bad')
    } finally {
      setImporting(false)
    }
  }

  async function saveRoom(participant: AccommodationAssignment, event: FormEvent) {
    event.preventDefault()
    const draft = drafts[participant.email]
    if (!draft || !isDirty(draft)) return

    const roomNumber = draft.status === 'own_arrangement' ? '' : draft.value.trim()
    const before = bucketOf(participant)
    setSavingEmail(participant.email)
    setCardErrors((current) => {
      const next = { ...current }
      delete next[participant.email]
      return next
    })
    try {
      const result = await api.assignAccommodationRoom(
        session.at, participant.email, roomNumber, draft.status,
      )
      /*
       * The row is patched in place and deliberately kept, even when it no longer
       * matches the active filter — assign a room under "No room yet" and the card
       * stays, now reading "Room 318". Dropping it would make the row the organiser
       * just touched vanish under their thumb, and the next tap would land on whatever
       * slid up into its place. The chip count decrementing is the signal that it
       * moved; the list re-narrows on the next search or filter change.
       */
      setParticipants((current) => current.map((row) => (
        row.email === participant.email
          ? { ...row, room_number: result.room_number, status: result.status }
          : row
      )))
      setDrafts((current) => {
        const next = { ...current }
        delete next[participant.email]
        return next
      })
      // One row changed; move it between buckets rather than re-count over the wire.
      const after = bucketOf(result)
      if (after !== before) {
        setCounts((current) => ({
          ...current,
          [before]: current[before] - 1,
          [after]: current[after] + 1,
        }))
      }
      setSavedEmail(participant.email)
      toast.show(
        result.status === 'own_arrangement'
          ? `${participant.name} is arranging their own accommodation`
          : result.room_number
            ? `Room ${result.room_number} assigned to ${participant.name}`
            : `Room assignment cleared for ${participant.name}`,
        'ok',
      )
    } catch (err) {
      setCardErrors((current) => ({
        ...current,
        [participant.email]: err instanceof ApiError && err.status === 403
          ? 'This account is not allowed to manage accommodation.'
          : 'Could not save. Try again.',
      }))
    } finally {
      setSavingEmail(null)
    }
  }

  /*
   * The key tick saves at once, outside the draft: it is a fact about the desk, not an
   * edit to think over, and the organiser is holding a queue while they tap it.
   */
  const [keyBusy, setKeyBusy] = useState<string | null>(null)
  async function toggleKey(participant: AccommodationAssignment, collected: boolean) {
    setKeyBusy(participant.email)
    setCardErrors((current) => {
      const next = { ...current }
      delete next[participant.email]
      return next
    })
    try {
      const result = await api.setAccommodationKey(session.at, participant.email, collected)
      setParticipants((current) => current.map((row) => (
        row.email === participant.email ? { ...row, key_collected_at: result.key_collected_at } : row
      )))
      setCounts((current) => ({
        ...current,
        keys_collected: current.keys_collected + (result.key_collected_at ? 1 : -1),
      }))
      toast.show(
        result.key_collected_at
          ? `Key handed to ${participant.name}`
          : `Key tick cleared for ${participant.name}`,
        'ok',
      )
    } catch (err) {
      setCardErrors((current) => ({
        ...current,
        [participant.email]: err instanceof ApiError && err.status === 409
          ? 'Assign a room first; there is no key to record.'
          : err instanceof ApiError && err.status === 403
            ? 'This account is not allowed to manage accommodation.'
            : 'Could not save. Try again.',
      }))
    } finally {
      setKeyBusy(null)
    }
  }

  /*
   * The denominator is the people who actually need a room, not the headcount. With 12
   * delegates bringing their own family the organiser's job is finished at 94 of 106,
   * and a meter measured against 106 could never fill — it would read as unfinished
   * work for the rest of the event. The own-arrangement figure is reported separately
   * so nobody thinks those twelve went missing.
   */
  const needRoom = counts.total - counts.own_arrangement
  const progress = needRoom > 0 ? Math.round((counts.assigned / needRoom) * 100) : 100
  const capped = total > participants.length
  /* Everything the organiser has to correct, in file order, whoever rejected it. */
  const importIssues = [...parseIssues, ...serverIssues].sort((a, b) => a.line - b.line)

  return (
    <main className="page accommodation-admin">
      <Masthead
        kicker="Organiser"
        title="Accommodation"
        sub="Assign a room number to a registered participant."
      />

      <section className="accommodation-admin__summary" aria-label="Room assignment progress">
        <div className="accommodation-admin__summary-head">
          <span className="accommodation-admin__intro-icon" aria-hidden="true">
            <Hotel size={20} strokeWidth={1.5} />
          </span>
          <div className="accommodation-admin__summary-copy">
            {/* "0 of 0 assigned" is true and unreadable, which is what the headline says
                once everybody left has declined a room. Name that state instead. */}
            <strong className="tnum">
              {counts.total === 0
                ? 'No participants'
                : needRoom === 0
                  ? 'No rooms to assign'
                  : `${counts.assigned} of ${needRoom} assigned`}
            </strong>
            <span>
              {[
                needRoom > 0 && counts.unassigned > 0
                  ? `${counts.unassigned} still without a room.`
                  : needRoom > 0 ? 'Everyone who needs a room has one.' : '',
                counts.own_arrangement > 0
                  ? `${counts.own_arrangement} arranging their own.`
                  : '',
                KEY_COLLECTION && counts.assigned > 0
                  ? `${counts.keys_collected} of ${counts.assigned} keys collected.`
                  : '',
              ].filter(Boolean).join(' ') || 'Nobody is registered for this event yet.'}
            </span>
          </div>
        </div>
        <div
          className="accommodation-admin__meter"
          role="progressbar"
          aria-valuenow={progress}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Participants needing a room who have one"
        >
          <span style={{ width: `${progress}%` }} />
        </div>
      </section>

      <form
        className="accommodation-admin__search"
        role="search"
        onSubmit={(event) => {
          event.preventDefault()
          void load(query, filter)
        }}
      >
        <label className="accommodation-admin__search-field">
          <span className="field__label kicker">Find a participant</span>
          <span className="accommodation-admin__search-input">
            <Search size={16} strokeWidth={1.7} aria-hidden="true" />
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Name or email — leave blank for everyone"
              autoComplete="off"
              enterKeyHint="search"
            />
            {query ? (
              <button
                type="button"
                className="accommodation-admin__clear"
                onClick={() => setQuery('')}
                aria-label="Clear search"
              >
                <X size={15} strokeWidth={2} />
              </button>
            ) : null}
          </span>
        </label>
        <div className="accommodation-admin__filters" role="group" aria-label="Filter by room status">
          {FILTERS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`accommodation-admin__chip${filter === option.value ? ' is-active' : ''}`}
              aria-pressed={filter === option.value}
              onClick={() => setFilter(option.value)}
            >
              {option.label}
              {option.value === 'unassigned' && counts.unassigned > 0 ? (
                <span className="tnum"> {counts.unassigned}</span>
              ) : option.value === 'nokey' && counts.assigned - counts.keys_collected > 0 ? (
                <span className="tnum"> {counts.assigned - counts.keys_collected}</span>
              ) : null}
            </button>
          ))}
        </div>
      </form>

      {dirtyCount > 0 ? (
        <div className="accommodation-admin__dirty" role="status">
          <TriangleAlert size={15} strokeWidth={1.8} aria-hidden="true" />
          {/* Not "rooms typed": a dirty row may be a room typed, a room cleared, or the
              own-arrangement toggle flipped, and the first wording was simply untrue for
              the other two. "Changes" covers all three without guessing which happened. */}
          <span>
            {dirtyCount} unsaved change{dirtyCount === 1 ? '' : 's'}.
          </span>
          <button type="button" onClick={discardDrafts}>Discard</button>
        </div>
      ) : null}

      {error ? (
        <p className="field__error accommodation-admin__error" role="alert">{error}</p>
      ) : null}

      {loading ? (
        <div className="accommodation-admin__results" aria-busy="true">
          {[0, 1, 2].map((item) => <Skeleton key={item} height="6.5rem" />)}
        </div>
      ) : null}

      {!loading && !error && participants.length === 0 ? (
        <Notice
          icon={<UserRound size={21} strokeWidth={1.4} />}
          title={query ? 'No participants found' : 'Nothing to show'}
          body={
            query
              ? 'Try a different name or email address.'
              : filter === 'unassigned'
                ? 'Everyone who needs a room has one.'
                : filter === 'own'
                  ? 'Nobody is marked as arranging their own accommodation.'
                  : 'No registered participants match this filter.'
          }
        />
      ) : null}

      {!loading && participants.length > 0 ? (
        <>
          <p className="accommodation-admin__count" aria-live="polite">
            {capped
              ? `Showing ${participants.length} of ${total}`
              : `${total} participant${total === 1 ? '' : 's'}`}
          </p>
          <section className="accommodation-admin__results" aria-label="Participants">
            {participants.map((participant) => {
              const draft = drafts[participant.email]
              const room = draft ? draft.value : participant.room_number ?? ''
              const dirty = draft ? isDirty(draft) : false
              const saving = savingEmail === participant.email
              const cardError = cardErrors[participant.email]
              const own = (draft ? draft.status : participant.status) === 'own_arrangement'
              const storedOwn = participant.status === 'own_arrangement'
              return (
                <article
                  className={`accommodation-admin__card${dirty ? ' is-dirty' : ''}`}
                  key={participant.email}
                >
                  <div className="accommodation-admin__person">
                    <span className="accommodation-admin__person-icon" aria-hidden="true">
                      <UserRound size={17} strokeWidth={1.5} />
                    </span>
                    <div className="accommodation-admin__person-copy">
                      <strong>{participant.name}</strong>
                      <span>{participant.email}</span>
                    </div>
                    {/* The stored room reads as a badge, so the input below is the only
                        place the value appears twice — it used to be repeated verbatim
                        in a status line under every card. "Own room" is a third state,
                        not an empty one: it means the work is done, not outstanding. */}
                    <span
                      className={
                        'accommodation-admin__badge'
                        + (storedOwn ? ' is-own' : participant.room_number ? ' is-set' : '')
                      }
                    >
                      {storedOwn
                        ? 'Own room'
                        : participant.room_number ? `Room ${participant.room_number}` : 'No room'}
                    </span>
                  </div>
                  <form
                    className="accommodation-admin__assignment"
                    onSubmit={(event) => void saveRoom(participant, event)}
                  >
                    <label className="accommodation-admin__room-field">
                      <span className="kicker">Room number</span>
                      <input
                        value={room}
                        onChange={(event) => editRoom(participant, event.target.value)}
                        placeholder={own ? 'Not needed' : 'e.g. 318'}
                        maxLength={50}
                        autoComplete="off"
                        inputMode="text"
                        disabled={own}
                      />
                    </label>
                    <Button
                      type="submit"
                      size="sm"
                      variant={dirty ? 'primary' : 'ink'}
                      busy={saving}
                      disabled={!dirty}
                      icon={
                        savedEmail === participant.email && !dirty
                          ? <Check size={15} strokeWidth={2.2} />
                          : <Save size={15} strokeWidth={1.7} />
                      }
                    >
                      {savedEmail === participant.email && !dirty ? 'Saved' : 'Save'}
                    </Button>
                  </form>
                  <label className="accommodation-admin__own">
                    <input
                      type="checkbox"
                      checked={own}
                      onChange={(event) => toggleOwnArrangement(participant, event.target.checked)}
                    />
                    <span>
                      Arranging their own accommodation
                      <small>Bringing family or booking privately — no room needed from us.</small>
                    </span>
                  </label>
                  {/* Only once a room is stored: the key exists then, and not before. */}
                  {KEY_COLLECTION && participant.room_number && !storedOwn ? (
                    <label className="accommodation-admin__own accommodation-admin__key" data-done={participant.key_collected_at ? '' : undefined}>
                      <input
                        type="checkbox"
                        checked={Boolean(participant.key_collected_at)}
                        disabled={keyBusy === participant.email}
                        onChange={(event) => void toggleKey(participant, event.target.checked)}
                      />
                      <span>
                        <span className="accommodation-admin__key-label"><KeyRound size={14} strokeWidth={1.8} aria-hidden="true" /> Key collected</span>
                        <small>
                          {participant.key_collected_at
                            ? `Handed over at ${keyTime(participant.key_collected_at)}. Untick if that was a mistake.`
                            : 'Tick when the delegate picks up the key at the hotel desk.'}
                        </small>
                      </span>
                    </label>
                  ) : null}
                  {dirty ? (
                    <p className="accommodation-admin__hint">
                      Unsaved. {draft?.storedStatus === 'own_arrangement'
                        ? 'Currently arranging their own.'
                        : draft?.stored
                          ? `Currently Room ${draft.stored}.`
                          : 'Currently no room.'}
                    </p>
                  ) : null}
                  {cardError ? (
                    <p className="field__error accommodation-admin__card-error" role="alert">
                      {cardError}
                    </p>
                  ) : null}
                </article>
              )
            })}
          </section>
          {capped ? (
            <div className="accommodation-admin__more">
              <Button
                type="button"
                variant="outline"
                busy={loadingMore}
                onClick={() => void load(query, filter, participants.length)}
              >
                Load {Math.min(PAGE_SIZE, total - participants.length)} more
              </Button>
            </div>
          ) : null}
        </>
      ) : null}

      {/* Loading a spreadsheet happens once per event; searching happens all day. The
          rare job stays available but out of the way of the frequent one. */}
      <details className="accommodation-admin__bulk">
        <summary>
          <span className="accommodation-admin__intro-icon" aria-hidden="true">
            <FileText size={19} strokeWidth={1.5} />
          </span>
          <span className="accommodation-admin__bulk-copy">
            <strong>Import an initial room list</strong>
            <span>Upload a CSV exported from Excel.</span>
          </span>
        </summary>

        <div className="accommodation-admin__bulk-body">
          <div className="accommodation-admin__bulk-actions">
            <label className="btn btn--outline accommodation-admin__file-button">
              Choose CSV
              <input type="file" accept=".csv,text/csv" onChange={(event) => void handleImportFile(event)} />
            </label>
            <a className="accommodation-admin__template" href="/accommodation-template.csv" download>
              Download template
            </a>
          </div>
          <p className="accommodation-admin__format">
            Required columns: <code>email,room_number</code>. Example: <code>participant@uitm.edu.my,318</code>.
          </p>
          {fileName ? (
            <p className="accommodation-admin__file-name">
              Selected: <strong>{fileName}</strong>
              <button type="button" onClick={clearImport}>Remove</button>
            </p>
          ) : null}

          {importRows.length > 0 || importIssues.length > 0 ? (
            <div className="accommodation-admin__preview" aria-live="polite">
              <div className="accommodation-admin__preview-topline">
                <strong>{importRows.length} valid row{importRows.length === 1 ? '' : 's'} ready</strong>
                {importIssues.length > 0 ? (
                  <span className="accommodation-admin__issue-count">
                    {importIssues.length} skipped
                  </span>
                ) : null}
              </div>
              {importRows.length > 0 ? (
                <div className="accommodation-admin__preview-table" role="table" aria-label="CSV preview">
                  <div className="accommodation-admin__preview-row accommodation-admin__preview-row--head" role="row">
                    <span role="columnheader">Email</span>
                    <span role="columnheader">Room</span>
                  </div>
                  {importRows.slice(0, 8).map((row) => (
                    <div className="accommodation-admin__preview-row" role="row" key={`${row.line}-${row.participant_email}`}>
                      <span role="cell">{row.participant_email}</span>
                      <span role="cell" className="tnum">{row.room_number}</span>
                    </div>
                  ))}
                </div>
              ) : null}
              {importRows.length > 8 ? <p className="accommodation-admin__preview-more">Showing the first 8 rows.</p> : null}
              {importIssues.length > 0 ? (
                <ul className="accommodation-admin__issues">
                  {importIssues.slice(0, 8).map((issue) => (
                    <li key={`${issue.line}-${issue.participant_email ?? 'row'}-${issue.reason}`}>
                      Row {issue.line}{issue.participant_email ? ` (${issue.participant_email})` : ''}: {issue.reason}
                    </li>
                  ))}
                </ul>
              ) : null}
              {importIssues.length > 8 ? <p className="accommodation-admin__preview-more">Showing the first 8 issues.</p> : null}
              {importResult ? (
                <p
                  className={importResult.imported + importResult.updated > 0
                    ? 'accommodation-admin__import-success'
                    : 'field__error'}
                  role="status"
                >
                  Imported {importResult.imported} new assignment{importResult.imported === 1 ? '' : 's'}
                  {' '}and updated {importResult.updated}.
                  {importIssues.length > 0
                    ? ` ${importIssues.length} row${importIssues.length === 1 ? '' : 's'} skipped — fix them below and upload again.`
                    : ''}
                </p>
              ) : null}
              {/* Bad rows no longer void the file: the valid ones import and the rest come
                  back listed above. Re-uploading a corrected file just updates them. */}
              <Button
                type="button"
                variant="primary"
                busy={importing}
                disabled={importRows.length === 0}
                onClick={() => void importRooms()}
                icon={<Save size={16} strokeWidth={1.7} />}
              >
                Import {importRows.length} room{importRows.length === 1 ? '' : 's'}
              </Button>
            </div>
          ) : null}
        </div>
      </details>
    </main>
  )
}
